import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import type { EventTypeUpsertInput } from '@cp3/shared';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';
import { useEffectiveUid, useIsAdminMode } from '../auth/TargetUidContext';

export interface EventTypeRow extends EventTypeUpsertInput {
  createdAt?: unknown;
  updatedAt?: unknown;
}

export function useEventTypes() {
  const { user } = useUser();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useQuery<EventTypeRow[]>({
    queryKey: ['event-types', targetUid] as const,
    enabled: !!user && !!targetUid,
    staleTime: 30_000,
    queryFn: async () => {
      if (adminMode && targetUid) {
        const fn = httpsCallable<{ uid: string }, { items: EventTypeRow[] }>(
          functions,
          'adminListEventTypes',
        );
        const r = await fn({ uid: targetUid });
        return r.data.items;
      }
      const fn = httpsCallable<unknown, { items: EventTypeRow[] }>(functions, 'listEventTypes');
      const r = await fn({});
      return r.data.items;
    },
  });
}

export function useUpsertEventType() {
  const qc = useQueryClient();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useMutation({
    mutationFn: async (input: EventTypeUpsertInput) => {
      if (adminMode && targetUid) {
        const fn = httpsCallable<EventTypeUpsertInput & { uid: string }, { ok: true }>(
          functions,
          'adminUpsertEventType',
        );
        await fn({ ...input, uid: targetUid });
        return;
      }
      const fn = httpsCallable<EventTypeUpsertInput, { ok: true }>(functions, 'upsertEventType');
      await fn(input);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['event-types', targetUid] }),
  });
}

export function useDeleteEventType() {
  const qc = useQueryClient();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useMutation({
    mutationFn: async (id: string) => {
      if (adminMode && targetUid) {
        const fn = httpsCallable<{ uid: string; id: string }, { ok: true }>(
          functions,
          'adminDeleteEventType',
        );
        await fn({ uid: targetUid, id });
        return;
      }
      const fn = httpsCallable<{ id: string }, { ok: true }>(functions, 'deleteEventType');
      await fn({ id });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['event-types', targetUid] }),
  });
}
